import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useShallow } from "zustand/react/shallow";

import { CameraAltOutlined, ErrorOutlined } from "@material-ui/icons";
import { Button } from "antd";
import { useAxisGridlineStore } from "stores/axisGridline/useAxisGridlineStore";
import { useAxisNameStore } from "stores/axisName/useAxisNameStore";
import { useDashboardScreenshotStore } from "stores/dashboardScreenshot/useDashboardScreenshotStore";
import styled from "styled-components";
import { sleep } from "utils/helper";
import { excludePropertiesByKeys } from "utils/objects";
import { getMergedSettings } from "utils/objects/getMergedSettings";

import { useLocalStorage, useUserEvents } from "hooks";

import { IChartScreenshotSettings } from "models/chart";
import {
  IScreenshotSettings,
  ScreenshotNotFocusedError,
  WidgetComponentType
} from "models/screenshot";

import {
  AutomationHandler,
  useRegisterHandlerWithBroker
} from "components/automation/contexts/AutomationBrokerContext";
import { AutomationCommand } from "components/automation/types/automation";
import { ChartScreenshotPayload } from "components/automation/types/payloads";
import { CommandResult, ImageResult } from "components/automation/types/results";
import { Topic, Topics } from "components/automation/types/topics";
import { ErrorBoundary, SubHeading } from "components/base";
import { copyDashboardToClipboard } from "components/dashboard/screenshot/copyDashboardToClipboard";
import "components/screenshot/utils/initializeContextExtractors";
import { extractWidgetScreenshotContext } from "components/screenshot/utils/widgetContextExtractors";

import ScreenshotSettingsForm from "./ScreenshotSettingsForm";
import { PresetSelect } from "./components";
import {
  ALL_POSSIBLE_GRIDLINE_KEYS,
  MPC_AXIS_TITLE_KEYS,
  MPC_X_AXIS_TITLE_KEYS
} from "./constants";
import {
  useScreenshotCalculatedDefaults,
  useScreenshotContext,
  useScreenshotDispatch,
  useScreenshotModeStyles,
  useScreenshotPresetContext,
  useToastScreenshotControl
} from "./hooks";
import {
  getEffectiveAxisNameId,
  getStoredDashboardScreenshotPresetKey,
  getStoredDashboardScreenshotSettingsKey,
  getStoredScreenshotPresetKey,
  getStoredScreenshotSettingsKey
} from "./utils";
import copyToClipboard from "./utils/copyToClipboard";

const resetFieldsKeys = [
  "width",
  "height",
  "titleFontSize",
  "axisFontSize",
  "axisLabelFontSize",
  "topsLabelsFontSize",
  "dataLabelsFontSize",
  "gridLabelsFontSize",
  "legendTitleSize",
  "legendFontSize"
];

/**
 * Main component for handling screenshot functionality including mode activation,
 * settings management, and toast control during screenshot mode.
 */

export default function ScreenshotActivity() {
  const {
    isChangingPreset,
    isResettingPreset,
    settings,
    widget,
    widgetState,
    screenshotOverlay
  } = useScreenshotContext();

  const isDashboardScreenshot =
    widget?.widgetComponentType === WidgetComponentType.Dashboard;

  // Dashboard screenshot state
  const deactivateDashboardScreenshot = useDashboardScreenshotStore((s) => s.deactivate);
  const activeWidgets = useDashboardScreenshotStore((s) => s.activeWidgets);
  const widgetBorderVisible = useDashboardScreenshotStore((s) => s.widgetBorderVisible);

  const defaultSettings = useScreenshotCalculatedDefaults();
  const { selectedPreset } = useScreenshotPresetContext();
  useScreenshotModeStyles();
  useToastScreenshotControl();

  // Local Storage
  const settingsKey = isDashboardScreenshot
    ? getStoredDashboardScreenshotSettingsKey(widget?.widgetId)
    : getStoredScreenshotSettingsKey(widget?.widgetId);
  const presetKey = isDashboardScreenshot
    ? getStoredDashboardScreenshotPresetKey(widget?.widgetId)
    : getStoredScreenshotPresetKey(widget?.widgetId);

  const {
    setStoredValue: setStoredScreenshotSettings,
    getStoredValue: getStoredScreenshot
  } = useLocalStorage(settingsKey, undefined);

  const { setStoredValue: setStoredPreset } = useLocalStorage(presetKey, undefined);

  // user events
  const { sendScreenshotEvent } = useUserEvents();

  // dispatch
  const screenshotDispatch = useScreenshotDispatch();

  // state
  const [copyError, setCopyError] = useState("");
  // Only keeps values that have been manually edited (overrides)
  const [formValues, setFormValues] = useState<Partial<IScreenshotSettings>>({});

  const [isGeneratingScreenshot, setIsGeneratingScreenshot] = useState(false);
  // EVA-6911: per-attempt generation counter guarding a still-in-flight
  // copyToClipboard() capture against firing completed-capture side effects
  // (success toast, telemetry, dispatch) once the user has clicked Cancel — or
  // once a newer Take Screenshot attempt has superseded it.
  //
  // A plain boolean ("cancelled") is not sufficient here: cancel attempt A,
  // then start attempt B (which would reset a boolean flag back to false),
  // then A's stale promise finally resolves — a boolean guard would see
  // `false` and wrongly let A fire success side effects on top of/instead of
  // B (an ABA race). A monotonically increasing id avoids this: each attempt
  // captures the counter's value at start, and only proceeds if the counter
  // still matches that value when its promise settles — handleCancel and
  // every new handleTakeScreenshot call both advance the counter, permanently
  // invalidating any earlier in-flight attempt's captured id.
  //
  // Component-local ephemeral UI state — stays a useRef, never promoted to
  // Zustand/Jotai/Redux (FR-009).
  const attemptIdRef = useRef(0);

  // Axis gridline store — single source of truth for all gridline overrides.
  const storeGridlines = useAxisGridlineStore(
    useShallow((s) => s.gridlines[widget?.widgetId ?? ""] ?? {})
  );
  const loadFromStorage = useAxisGridlineStore((s) => s.loadFromStorage);

  const ws = widgetState as {
    multiProductActive?: boolean;
    request?: { chartSetting?: { product?: string } };
  };
  const effectiveAxisNameId = getEffectiveAxisNameId(
    widget?.widgetId,
    ws?.multiProductActive,
    ws?.request?.chartSetting?.product
  );

  // Axis name store — persists axis title overrides across sessions.
  const storeAxisNames = useAxisNameStore(
    useShallow((s) => s.axisNames[effectiveAxisNameId] ?? {})
  );
  const loadAxisNamesFromStorage = useAxisNameStore((s) => s.loadFromStorage);
  const axisNameResetKeys = useAxisNameStore((s) => s.resetKeys);
  // Merged settings = calculated defaults + user overrides. Memoized so its
  // reference is stable across unrelated re-renders — otherwise a fresh object
  // each render defeats the form context's memo and re-renders every setting item.
  const mergedValues = useMemo(
    () => getMergedSettings(defaultSettings, formValues) as Record<string, unknown>,
    [defaultSettings, formValues]
  );

  const handleScreenshotError = useCallback((err: unknown) => {
    if (err instanceof ScreenshotNotFocusedError) {
      setCopyError("Please focus on the browser while the screenshot is being taken.");
    } else {
      setCopyError("Error copying screenshot to clipboard.");
    }
  }, []);

  const handler: AutomationHandler = {
    id: "chart-screenshot",
    handles: function (topic: Topic): boolean {
      return topic === Topics.TakeChartScreenshot || topic === Topics.TakeMapScreenshot;
    },
    executeCommand: async function (command: AutomationCommand): Promise<CommandResult> {
      const settings = command.payload as ChartScreenshotPayload;
      setFormValues(settings);
      await sleep(8000);

      const result = await handleTakeScreenshot(true);
      if (!result) {
        return {
          status: "error",
          error: "unable to take screenshot"
        };
      }
      return Promise.resolve({
        status: "ok",
        payload: {
          title: (command.payload as ChartScreenshotPayload)?.title,
          blob: result
        } as ImageResult
      });
    }
  };

  useRegisterHandlerWithBroker(handler);

  const handleTakeScreenshot = useCallback(
    async (automation?: boolean, overrideSettings?: IScreenshotSettings) => {
      const attemptId = ++attemptIdRef.current;
      setIsGeneratingScreenshot(true);
      try {
        const settingsToUse = { ...settings, automation, ...overrideSettings };
        const blob = await copyToClipboard(
          widget?.widgetComponentType,
          widgetState,
          settingsToUse,
          screenshotOverlay
        );

        // Cancel (or a newer Take Screenshot attempt) may have advanced
        // attemptIdRef past what this call captured while copyToClipboard()
        // was in flight — skip every completed-capture side effect below
        // (FR-006) without affecting the overlay, which handleCancel already
        // cleared immediately. Returning `undefined` here means a superseded
        // automation attempt (executeCommand above) intentionally reports
        // failure to its caller — only one attempt per chain is ever allowed
        // to "win" and return a blob.
        if (attemptId !== attemptIdRef.current) {
          return;
        }

        setCopyError("");
        toast.success("Screenshot copied to clipboard");

        const widgetSpecificContext = extractWidgetScreenshotContext(
          widget?.widgetComponentType,
          widgetState,
          settingsToUse
        );

        sendScreenshotEvent({
          widgetId: widget?.widgetId,
          widgetType: widget?.widgetComponentType,
          presetUsed: !!selectedPreset,
          presetName: selectedPreset?.title,
          legendsEnabled: settingsToUse?.legendVisible ?? false,
          abbreviationsUsed: settingsToUse?.applyAbbreviations ?? false,
          width: settingsToUse?.width,
          height: settingsToUse?.height,
          ...widgetSpecificContext
        });

        screenshotDispatch({
          payload: {
            widget: null,
            widgetState: null
          }
        });
        return blob;
      } catch (err) {
        // A cancelled (or superseded) attempt that later rejects — e.g. a late
        // ScreenshotNotFocusedError — must not resurrect an error the user
        // already cancelled away, nor stomp on a newer attempt's own error.
        if (attemptId === attemptIdRef.current) {
          handleScreenshotError(err);
        }
      } finally {
        // Guarded the same way — a stale/superseded attempt settling late must
        // not clear the loading state out from under a newer, still-in-flight
        // attempt. handleCancel already clears it synchronously and
        // immediately (FR-007), independent of this.
        if (attemptId === attemptIdRef.current) {
          setIsGeneratingScreenshot(false);
        }
      }
    },
    [
      settings,
      widget?.widgetComponentType,
      widget?.widgetId,
      widgetState,
      screenshotOverlay,
      screenshotDispatch,
      selectedPreset,
      sendScreenshotEvent
    ]
  );

  const handleCancel = useCallback(() => {
    // Advances past any in-flight attempt's captured attemptId, permanently
    // invalidating it — see the attemptIdRef comment above for why a plain
    // boolean flag isn't sufficient here.
    attemptIdRef.current += 1;
    setIsGeneratingScreenshot(false);
    setCopyError("");
    if (isDashboardScreenshot) {
      deactivateDashboardScreenshot();
    }
    screenshotDispatch({
      payload: {
        widget: null,
        widgetState: null
      }
    });
  }, [screenshotDispatch, isDashboardScreenshot, deactivateDashboardScreenshot]);

  const handleDashboardScreenshot = useCallback(async () => {
    // Same per-attempt generation-counter guard as handleTakeScreenshot —
    // shares attemptIdRef/handleCancel so a cancel-during-capture in
    // dashboard screenshot mode is guarded too.
    const attemptId = ++attemptIdRef.current;
    setIsGeneratingScreenshot(true);
    try {
      await copyDashboardToClipboard();

      if (attemptId !== attemptIdRef.current) {
        return;
      }

      setCopyError("");
      toast.success("Dashboard screenshot copied to clipboard");

      sendScreenshotEvent({
        widgetId: widget?.widgetId,
        widgetType: "dashboard",
        presetUsed: !!selectedPreset,
        presetName: selectedPreset?.title,
        widgetCount: activeWidgets.length,
        widgetTypes: activeWidgets.map((w) => w.type),
        widgetBorderVisible,
        legendsEnabled: {
          upstream: settings?.showLegendInScreenshot ?? false,
          midstream: settings?.showMidstreamLegendInScreenshot ?? false,
          ipdb: settings?.showIpdbLegendInScreenshot ?? false,
          networkDepth: settings?.showNetworkDepthLegendInScreenshot ?? false,
          shapefile: settings?.showShapefileLegendInScreenshot ?? false
        },
        abbreviationsUsed: settings?.applyAbbreviations ?? false
      });

      deactivateDashboardScreenshot();
      screenshotDispatch({
        payload: {
          widget: null,
          widgetState: null
        }
      });
    } catch (err) {
      if (attemptId === attemptIdRef.current) {
        handleScreenshotError(err);
      }
    } finally {
      if (attemptId === attemptIdRef.current) {
        setIsGeneratingScreenshot(false);
      }
    }
  }, [
    activeWidgets,
    deactivateDashboardScreenshot,
    screenshotDispatch,
    handleScreenshotError,
    sendScreenshotEvent,
    selectedPreset,
    settings,
    widget?.widgetId,
    widgetBorderVisible
  ]);

  // Handle preset reset and preset change, then keep Redux screenshot settings in sync.
  useEffect(() => {
    if (isResettingPreset) {
      const keysToExclude = widgetState?.multiProductActive
        ? [...resetFieldsKeys, ...MPC_AXIS_TITLE_KEYS]
        : resetFieldsKeys;
      const fieldsToKeep = excludePropertiesByKeys(formValues, keysToExclude);

      setStoredScreenshotSettings(fieldsToKeep);
      setFormValues(fieldsToKeep);

      screenshotDispatch({
        payload: {
          isResettingPreset: false
        }
      });
    }

    if (isChangingPreset) {
      const keysToExclude = widgetState?.multiProductActive
        ? [...resetFieldsKeys, ...MPC_AXIS_TITLE_KEYS]
        : resetFieldsKeys;
      const fieldsToKeep = excludePropertiesByKeys(formValues, keysToExclude);

      setFormValues(fieldsToKeep);
      setStoredScreenshotSettings(fieldsToKeep);
      setStoredPreset({ ...selectedPreset });

      screenshotDispatch({
        payload: {
          isChangingPreset: false
        }
      });
    }

    // Keep Redux screenshot settings up to date so the live preview reflects
    // both calculated defaults and user overrides. getMergedSettings returns a
    // loosely-typed merge of the (untyped) calculated defaults + overrides, so we
    // assert the settings shape at this boundary.
    const nextSettings = getMergedSettings(
      defaultSettings,
      formValues
    ) as unknown as IChartScreenshotSettings;
    screenshotDispatch({ payload: { settings: nextSettings } });
  }, [defaultSettings, isChangingPreset, isResettingPreset, selectedPreset, formValues]);

  // Handle initial load — restore persisted overrides from localStorage.
  // Axis title overrides live in the axis name store now — strip them so stale
  // screenshotSettings values can't shadow the store on first render.
  useEffect(() => {
    const storedSettings = getStoredScreenshot() ?? {};
    setFormValues(excludePropertiesByKeys(storedSettings, MPC_AXIS_TITLE_KEYS));
  }, []);

  // When screenshot mode activates for an MPC chart, sync mergeSameUnitAxes to the
  // chart's current combineSameUnitAxis so the toggle always reflects the live chart
  // state on entry, overriding any stale value from a previous screenshot session.
  useEffect(() => {
    if (!widgetState?.multiProductActive) return;
    const combineSameUnitAxis =
      (widgetState?.request?.combineSameUnitAxis as boolean) ?? false;
    setFormValues((prev) => {
      if ((prev as Record<string, unknown>).mergeSameUnitAxes === combineSameUnitAxis)
        return prev;
      return { ...prev, mergeSameUnitAxes: combineSameUnitAxis };
    });
  }, [widgetState?.multiProductActive]);

  // When the user toggles "Merge Same Unit Axes", clear X axis title overrides so
  // the new X axis layout's calculated defaults take over. Y axis overrides are
  // unaffected — only the X axes change when combining axes.
  //
  // Guard: skip if the form value is merely syncing to match the live chart state
  // (e.g. correcting a stale stored value on mount). A real user toggle always moves
  // *away* from the chart's current combineSameUnitAxis, while a sync correction moves
  // *toward* it.
  const prevMergeSameUnitAxesRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    const current = mergedValues.mergeSameUnitAxes as boolean | undefined;
    const prev = prevMergeSameUnitAxesRef.current;
    prevMergeSameUnitAxesRef.current = current;
    if (prev === undefined || current === prev) return;
    if (!widgetState?.multiProductActive) return;
    const chartCombineState =
      (widgetState?.request?.combineSameUnitAxis as boolean) ?? false;
    if (current === chartCombineState) return; // sync correction, not a user toggle
    setFormValues((fv) => excludePropertiesByKeys(fv, MPC_X_AXIS_TITLE_KEYS));
    const storedSettings = getStoredScreenshot() ?? {};
    setStoredScreenshotSettings(
      excludePropertiesByKeys(storedSettings, MPC_X_AXIS_TITLE_KEYS)
    );
    if (widget?.widgetId) {
      axisNameResetKeys(widget.widgetId, MPC_X_AXIS_TITLE_KEYS);
    }
  }, [mergedValues.mergeSameUnitAxes]);

  // Ensure the stores are current whenever the active widget changes.
  useEffect(() => {
    if (widget?.widgetId) {
      loadFromStorage(widget.widgetId);
    }
  }, [widget?.widgetId, loadFromStorage]);

  useEffect(() => {
    if (effectiveAxisNameId) {
      loadAxisNamesFromStorage(effectiveAxisNameId);
    }
  }, [effectiveAxisNameId, loadAxisNamesFromStorage]);

  // Store is the sole authority for gridline state in all modes.
  // Always clear stale gridline keys from formValues before applying store overrides so that
  // a store reset (e.g. after an axis-layout change) propagates correctly and stale false-values
  // from a prior MPC session never override the calculated defaults for a single-product chart.
  useEffect(() => {
    if (!widget?.widgetId) return;
    setFormValues((prev) => {
      const cleared = excludePropertiesByKeys(prev, ALL_POSSIBLE_GRIDLINE_KEYS);
      return { ...cleared, ...storeGridlines };
    });
  }, [storeGridlines, widget?.widgetId]);

  // Sync stored axis name overrides into formValues for all chart modes.
  // When entering MPC mode, also purge stale single-product axis names from localStorage
  // so calculated defaults take over; the store's MPC names are then spread in below.
  // Merging the former "MPC strip" effect here removes the implicit ordering dependency
  // that previously existed between two separate effects.
  useEffect(() => {
    if (!widget?.widgetId) return;
    if (widgetState?.multiProductActive) {
      const storedSettings = getStoredScreenshot() ?? {};
      setStoredScreenshotSettings(
        excludePropertiesByKeys(storedSettings, MPC_AXIS_TITLE_KEYS)
      );
    }
    setFormValues((prev) => {
      const cleared = excludePropertiesByKeys(prev, MPC_AXIS_TITLE_KEYS);
      return { ...cleared, ...storeAxisNames };
    });
  }, [storeAxisNames, widget?.widgetId, widgetState?.multiProductActive]);

  return (
    <ErrorBoundary>
      <ActivityWrapper className="screenshot-activity-panel">
        <ScreenshotHeaderContainer className="screenshot-header">
          <SubHeading>
            {isDashboardScreenshot ? "Dashboard Screenshot" : "Screenshot Settings"}
          </SubHeading>
          {!isDashboardScreenshot && (
            <PresetContainer>
              <PresetSelect />
            </PresetContainer>
          )}
        </ScreenshotHeaderContainer>
        <SettingsContainer className="screenshot-settings">
          <ScreenshotSettingsForm values={mergedValues} setFormValues={setFormValues} />
        </SettingsContainer>
        <FooterContainer className="screenshot-footer">
          {copyError && (
            <ErrorContainer>
              <ErrorOutlined /> {copyError}
            </ErrorContainer>
          )}
          <ActionsContainer>
            <StyledButton
              type="default"
              onClick={handleCancel}
              data-testid="cancel-screenshot"
              data-qa="cancel-screenshot">
              Cancel
            </StyledButton>
            <StyledButton
              type="primary"
              loading={isGeneratingScreenshot}
              onClick={
                isDashboardScreenshot
                  ? handleDashboardScreenshot
                  : () => handleTakeScreenshot(false)
              }
              data-testid="take-screenshot"
              data-qa="take-screenshot"
              disabled={isGeneratingScreenshot}>
              <CameraAltOutlined /> Take Screenshot
            </StyledButton>
          </ActionsContainer>
        </FooterContainer>
      </ActivityWrapper>
    </ErrorBoundary>
  );
}

const ActivityWrapper = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  height: 100%;
  width: 100%;
`;

const ScreenshotHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 10px 8px 20px;
  border-bottom: 1px solid #e2e5e6;
  align-items: center;
  gap: 12px;
  justify-content: space-between;

  h3 {
    margin: 0 auto 0 0;
    line-height: 32px;
  }
`;

const PresetContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 5px;
  justify-content: space-between;

  button {
    color: #a2aaad;
    display: flex;
    align-items: center;
    padding: 2px;
  }
`;

const SettingsContainer = styled.div`
  height: 100%;
  overflow-y: auto;

  .ant-collapse-header-text {
    font-weight: 800;
  }

  .ant-input-suffix {
    opacity: 0.6;
  }

  .ant-space-compact {
    button.ant-btn {
      display: flex;
      align-items: center;
      padding: 3px 0;
    }
  }
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 10px 8px 20px;
  border-top: 1px solid #e2e5e6;
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;

  .ant-btn {
    border-radius: 5px;

    > span {
      margin-left: 0;
    }
  }

  .ant-btn:hover {
    border-radius: 5px;
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }

  button[disabled],
  button[disabled]:hover {
    background-color: #a2aaad;
    border-color: rgba(0, 0, 0, 0.15);
    color: white;
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  padding: 8px 0;
  color: var(--color-danger);
  font-weight: var(--fontWeightMedium);
  text-align: left;
`;
